import { useMemo } from 'react';
import type { ReactNode } from 'react';
import type { DocumentBlock } from '@platform/shared-types';
import { diffBlocks, summariseDiff } from './diff';
import type { BlockChange, BlockDiffEntry } from './diff';
import type { WordDiffOp } from './wordDiff';
import { BlockView } from './BlockView';
import { ChangeMark, TypeBadge } from './BlockEditor';

/**
 * The two-column version compare from mockup screen 3.
 *
 * The left column is the older version, the right the newer. Blocks are matched
 * by their stable id, so a block that merely moved shows as unchanged in both
 * columns and only genuinely edited text carries the CHANGED marker. That is
 * the whole point of the id: "Clause 4.2 changed", not "everything shifted".
 *
 * See the PALETTE RULE in ./ui.tsx before adding a class.
 */

const ROW_TONE: Record<BlockChange, string> = {
  unchanged: 'border-dark-700 bg-white',
  changed: 'border-amber-500/40 bg-amber-500/5',
  added: 'border-emerald-500/40 bg-emerald-500/5',
  removed: 'border-red-500/30 bg-red-500/5 opacity-60',
};

/**
 * Word-level render of a `changed` block's `text`, from `diffBlocks`'s
 * `wordDiff` field. Colour is never the only signal here — additions also
 * carry an underline, removals a strikethrough — the same "a second, non-
 * colour channel carries the meaning" rule `calloutTones.ts` documents for
 * this same colour-blind-safe reason.
 *
 * Contrast on the `bg-white`/light legal-page surface these rows render on
 * (see `ROW_TONE` above): emerald-800 text on emerald-50 ~7.3:1, red-800 on
 * red-50 ~7.6:1 — both comfortably clear WCAG AA's 4.5:1 minimum for body
 * text (computed the same way as the ratios noted in `calloutTones.ts`).
 */
function WordDiffText({ ops }: { ops: WordDiffOp[] }) {
  return (
    <p className="mt-1.5 whitespace-pre-wrap break-words rounded bg-stone-50 p-2 text-[12.5px] leading-relaxed text-stone-800">
      {ops.map((op, i) => {
        if (op.kind === 'same') return <span key={i}>{op.text}</span>;
        if (op.kind === 'added') {
          return (
            <span
              key={i}
              className="rounded-sm bg-emerald-50 text-emerald-800 underline decoration-2 underline-offset-2"
            >
              {op.text}
            </span>
          );
        }
        return (
          <span key={i} className="rounded-sm bg-red-50 text-red-800 line-through decoration-2">
            {op.text}
          </span>
        );
      })}
    </p>
  );
}

function DiffRow({
  entry, struck = false,
}: { entry: BlockDiffEntry<DocumentBlock>; struck?: boolean }) {
  return (
    <div className={`flex items-start gap-2.5 rounded-lg border p-3 ${ROW_TONE[entry.change]}`}>
      <TypeBadge block={entry.block} />
      <div className="min-w-0 flex-1">
        <BlockView block={entry.block} struck={struck} />
        {entry.change === 'changed' && entry.wordDiff && <WordDiffText ops={entry.wordDiff} />}
        <div className="mt-1.5 font-mono text-[10px] text-stone-500">{entry.block.id}</div>
      </div>
      <ChangeMark change={entry.change} />
    </div>
  );
}

function Column({
  label, children,
}: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h5 className="mb-2.5 text-[11px] font-semibold uppercase tracking-widest text-stone-500">
        {label}
      </h5>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export interface VersionDiffProps {
  prevBlocks: readonly DocumentBlock[];
  nextBlocks: readonly DocumentBlock[];
  /** e.g. "v3 — published, active" */
  prevLabel?: ReactNode;
  /** e.g. "v4 — draft" */
  nextLabel?: ReactNode;
  /** Hide rows that did not change, so a long policy shows only the delta. */
  changesOnly?: boolean;
  className?: string;
}

export function VersionDiff({
  prevBlocks, nextBlocks,
  prevLabel = 'Previous version', nextLabel = 'This version',
  changesOnly = false, className = '',
}: VersionDiffProps) {
  const entries = useMemo(() => diffBlocks(prevBlocks, nextBlocks), [prevBlocks, nextBlocks]);
  const summary = useMemo(() => summariseDiff(entries), [entries]);

  // Left column walks `prev` in prev's own order; a block present in both is
  // tagged with the change so both sides carry the same marker.
  const changeById = useMemo(() => {
    const m = new Map<string, BlockChange>();
    for (const e of entries) m.set(e.block.id, e.change);
    return m;
  }, [entries]);

  // `entries` only carries `wordDiff` on the `next`-side block for a changed
  // id; the left column below reconstructs its own entries from `prevBlocks`,
  // so it needs its own lookup to show the same word-level detail.
  const wordDiffById = useMemo(() => {
    const m = new Map<string, BlockDiffEntry<DocumentBlock>['wordDiff']>();
    for (const e of entries) if (e.wordDiff) m.set(e.block.id, e.wordDiff);
    return m;
  }, [entries]);

  const left: BlockDiffEntry<DocumentBlock>[] = prevBlocks
    .map((block) => ({
      block,
      change: changeById.get(block.id) ?? 'unchanged',
      wordDiff: wordDiffById.get(block.id),
    }))
    .filter((e) => !changesOnly || e.change !== 'unchanged');

  const right: BlockDiffEntry<DocumentBlock>[] = entries
    .filter((e) => e.change !== 'removed')
    .filter((e) => !changesOnly || e.change !== 'unchanged');

  return (
    <div className={className}>
      <p className="mb-3 text-[12.5px] text-stone-400">
        {summary.identical ? (
          <span>No block differs between these two versions.</span>
        ) : (
          <>
            <span className="font-semibold text-amber-400">{summary.changed} changed</span>
            {' · '}
            <span className="font-semibold text-emerald-400">{summary.added} added</span>
            {' · '}
            <span className="font-semibold text-red-300">{summary.removed} removed</span>
            {' · '}
            <span>{summary.unchanged} unchanged</span>
          </>
        )}
      </p>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Column label={prevLabel}>
          {left.length === 0
            ? <p className="text-[12.5px] text-stone-500">Nothing on this side.</p>
            : left.map((e) => (
              <DiffRow key={`prev-${e.block.id}`} entry={e} struck={e.change === 'removed'} />
            ))}
        </Column>

        <Column label={nextLabel}>
          {right.length === 0
            ? <p className="text-[12.5px] text-stone-500">Nothing on this side.</p>
            : right.map((e) => <DiffRow key={`next-${e.block.id}`} entry={e} />)}
        </Column>
      </div>

      <div className="mt-4 rounded-lg border border-violet-500/35 border-l-[3px] border-l-violet-500 bg-violet-500/10 px-3.5 py-3 text-[12.5px] text-stone-400">
        <b className="text-violet-300">Why the ids matter.</b> A block id that exists in both
        versions is reported as one changed clause. Without it the system would have to re-diff the
        whole document as text and every reorder would read as a rewrite.
      </div>
    </div>
  );
}
